import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronLeft, Sparkles, Layers } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MaterialCard } from './MaterialCard';
import { useSEO } from '../utils/useSEO';

import { BACKEND_URL } from "../utils/backendUrl";
const API = `${BACKEND_URL}/api`;

// Brand metadata - extends the home cards with full landing data
const BRANDS = {
  esun: {
    name: "eSUN",
    color: "#00a651",
    tagline: "FILAMENTOS DE ALTA CALIDAD",
    description: "eSUN es un fabricante chino líder en filamentos de impresión 3D con más de una década de experiencia. Sus filamentos PLA+, PETG, ABS+, TPU y especialidades como eSilk, eMarble y ePLA-CF destacan por su consistencia, amplia gama de colores y compatibilidad universal con impresoras FDM.",
    searchTerm: "eSUN",
    website: "https://www.esun3d.com/",
    highlight: true,
    badge: "NUEVA COLABORACIÓN",
    seoTitle: "eSUN — Filamentos PLA+, PETG, ABS+, TPU | PrintMaterial Pro",
    seoDescription: "Catálogo completo de filamentos eSUN: PLA+, PETG, ABS+, TPU 95A, eSilk-PLA, eMarble, ePLA-CF, ePA Nylon y ePC Policarbonato. Compra directa en esun3d.com.",
    features: ["Aprobado FDA", "Más de 10 años de experiencia", "Amplia gama de colores", "Especialidades: eSilk, eMarble, eCF"],
  },
  bambu: {
    name: "Bambu Lab",
    color: "#1a8ccc",
    tagline: "IMPRESIÓN 3D DE ALTA VELOCIDAD",
    description: "Bambu Lab ha revolucionado la impresión 3D con sus impresoras de altísima velocidad y los filamentos optimizados que las acompañan. PLA Basic, ABS, PETG-HF, TPU 95A y ASA son los buques insignia, distribuidos en España vía SICNOVA.",
    searchTerm: "Bambu",
    website: "https://eu.store.bambulab.com/",
    badge: "ALTA VELOCIDAD",
    seoTitle: "Bambu Lab — Filamentos PLA, ABS, PETG, TPU, ASA | PrintMaterial Pro",
    seoDescription: "Filamentos Bambu Lab disponibles en España vía SICNOVA: PLA Basic, ABS, PETG-HF, TPU 95A, ASA. Optimizados para impresoras Bambu Lab P1S, X1, A1.",
    features: ["Optimizado para velocidad", "Sistema AMS compatible", "Distribución SICNOVA España", "Ecosistema completo"],
  },
  formlabs: {
    name: "Formlabs",
    color: "#7e57c2",
    tagline: "RESINAS SLA PROFESIONALES",
    description: "Formlabs es referencia mundial en impresión SLA profesional. Sus resinas biocompatibles, dentales y técnicas (BioMed, Dental LT, Tough 2000, Grey Pro) abren puertas a aplicaciones médicas, dentales e industriales con precisión micrométrica.",
    searchTerm: "Formlabs",
    website: "https://formlabs.com/",
    badge: "BIOCOMPATIBLE",
    seoTitle: "Formlabs — Resinas SLA Biocompatibles, Dentales, Técnicas | PrintMaterial Pro",
    seoDescription: "Resinas Formlabs para Form 4, Form 4B, Form 3B+ y Form 3L: Dental LT Clear, BioMed Amber, Tough 2000, Grey Pro. Compra en SICNOVA.",
    features: ["USP Clase VI biocompatible", "Aplicaciones dentales", "Esterilización autoclave", "Form 4 / Form 3B+ / Form 3L"],
  },
  sicnova: {
    name: "SICNOVA",
    color: "#f59e0b",
    tagline: "DISTRIBUIDOR ESPAÑOL DE REFERENCIA",
    description: "SICNOVA 3D es el distribuidor español de referencia en impresión 3D profesional. Maneja marcas como BambuLab, Formlabs, Markforged, BASF Forward AM y Polymaker, ofreciendo soporte técnico, formación y postventa local.",
    searchTerm: "SICNOVA",
    website: "https://tienda.sicnova3d.com/",
    badge: "DISTRIBUIDOR ESPAÑA",
    seoTitle: "SICNOVA 3D — Distribuidor BambuLab, Formlabs, Markforged | PrintMaterial Pro",
    seoDescription: "SICNOVA 3D distribuye en España BambuLab, Formlabs, Markforged, BASF, Polymaker. Soporte técnico, formación y postventa local.",
    features: ["Soporte técnico en España", "Formación profesional", "Postventa local", "Multi-marca"],
  },
};

// Resolve incoming slug (case-insensitive) to brand key
const resolveBrand = (slug) => {
  if (!slug) return null;
  const lower = slug.toLowerCase();
  if (lower === "esun") return BRANDS.esun;
  if (lower === "bambu" || lower === "bambulab" || lower === "bambu-lab") return BRANDS.bambu;
  if (lower === "formlabs") return BRANDS.formlabs;
  if (lower === "sicnova") return BRANDS.sicnova;
  return null;
};

const BrandPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const brand = resolveBrand(slug);
  const [materials, setMaterials] = useState([]);
  const [stats, setStats] = useState({ today: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  // Always call useSEO (Hook order rule). Uses safe fallbacks if brand is unknown.
  useSEO({
    title: brand ? brand.seoTitle : "Marca no encontrada | PrintMaterial Pro",
    description: brand ? brand.seoDescription : "La marca solicitada no existe en nuestro catálogo.",
    canonical: brand ? `${window.location.origin}/brand/${slug}` : `${window.location.origin}/`,
  });

  useEffect(() => {
    if (!brand) { setLoading(false); return; }
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [matsRes, statsRes] = await Promise.all([
          axios.get(`${API}/materials?search=${encodeURIComponent(brand.searchTerm)}`),
          axios.get(`${API}/stats/brand-clicks`),
        ]);
        if (!cancelled) {
          setMaterials(matsRes.data.materials || matsRes.data || []);
          const clickKey = brand.name.toLowerCase().replace(" ", "");
          const mappedKey = clickKey === "bambulab" ? "bambulab" : clickKey;
          setStats(statsRes.data.by_brand?.[mappedKey] || { today: 0, total: 0 });
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('BrandPage fetch failed:', err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [brand]);

  if (!brand) {
    return (
      <div className="min-h-screen pt-24 px-6 flex items-center justify-center" data-testid="brand-not-found">
        <div className="text-center">
          <h1 className="text-3xl mb-4">Marca no encontrada</h1>
          <p className="text-muted-foreground mb-6">La marca <span className="font-mono">{slug}</span> no existe en nuestro catálogo.</p>
          <Button onClick={() => navigate("/")} className="bg-[#1a8ccc] hover:bg-[#1575b0] text-white">
            Volver a la home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid={`brand-page-${slug.toLowerCase()}`}>
      {/* Hero */}
      <section className="relative px-6 py-16" style={{ background: `linear-gradient(135deg, ${brand.color}15 0%, transparent 60%)` }}>
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Volver a inicio
          </Link>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 space-y-6 animate-fade-in-up">
              <Badge variant="outline" className="badge-industrial" style={{ borderColor: brand.color, color: brand.color }}>
                <Sparkles className="w-3 h-3 mr-2" />
                {brand.badge}
              </Badge>

              <h1 className="text-5xl lg:text-7xl leading-none">
                <span style={{ color: brand.color }}>{brand.name.toUpperCase()}</span>
              </h1>

              <p className="text-lg uppercase tracking-widest text-muted-foreground">
                {brand.tagline}
              </p>

              <p className="text-base text-foreground max-w-2xl">
                {brand.description}
              </p>

              <div className="flex flex-wrap gap-3">
                {brand.features.map((f) => (
                  <span key={f} className="px-3 py-1 text-xs font-mono border border-border rounded-sm bg-card">
                    {f}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href={brand.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-sm text-white transition-colors"
                  style={{ backgroundColor: brand.color }}
                  data-testid={`visit-website-${slug.toLowerCase()}`}
                >
                  VISITAR WEB OFICIAL
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <Link
                  to={`/catalog?brand=${encodeURIComponent(brand.searchTerm)}`}
                  className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-sm border border-input bg-background hover:bg-accent transition-colors"
                >
                  VER EN CATÁLOGO
                </Link>
              </div>
            </div>

            {/* Live stats card */}
            <div className="bg-card border border-border p-6 space-y-4 lg:sticky lg:top-24" data-testid={`brand-stats-${slug.toLowerCase()}`}>
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground">Engagement en vivo</h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: brand.color }}></span>
                      <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: brand.color }}></span>
                    </span>
                    <span className="text-xs text-muted-foreground">VISITAS HOY</span>
                  </div>
                  <span className="text-3xl font-mono font-bold" style={{ color: brand.color }}>
                    {stats.today}
                  </span>
                </div>

                <div className="flex items-center justify-between border-t border-border pt-3">
                  <span className="text-xs text-muted-foreground">CLICKS TOTALES</span>
                  <span className="text-2xl font-mono">{stats.total}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Layers className="w-3 h-3" />
                    MATERIALES
                  </span>
                  <span className="text-2xl font-mono">{materials.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials grid */}
      <section className="px-6 py-12 max-w-7xl mx-auto">
        <h2 className="text-3xl mb-2">CATÁLOGO {brand.name.toUpperCase()}</h2>
        <p className="text-muted-foreground mb-8">
          {materials.length} {materials.length === 1 ? "material disponible" : "materiales disponibles"}
        </p>

        {loading ? (
          <div className="catalog-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="loading-skeleton h-64 rounded-sm" />
            ))}
          </div>
        ) : (
          <div className="catalog-grid">
            {materials.map((m) => (
              <MaterialCard
                key={m.id}
                material={m}
                onSelect={() => navigate(`/material/${m.id}`)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default BrandPage;
